import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import styles from "./AppVersionsScreen.module.scss";
import classNames from "classnames";
import appVersionsService from "../../../apis/services/admin/appVersionsService";
import { toast } from "react-toastify";
import BaseModal from "../../../components/modals/baseModal/BaseModal";
import moment from "moment";
import { useHistory } from "react-router-dom";

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return moment(dateString).format("DD.MM.YYYY");
};

// Truncate URL helper
const truncateUrl = (url, maxLen = 50) => {
  if (!url) return "-";
  if (url.length <= maxLen) return url;
  return url.substring(0, maxLen) + "...";
};

const AppVersionsScreen = () => {
  const history = useHistory();
  const [versions, setVersions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [devicesModalOpen, setDevicesModalOpen] = useState(false);
  const [selectedVersionForDevices, setSelectedVersionForDevices] = useState(null);
  const [devicesOnVersion, setDevicesOnVersion] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Form states
  const [formVersion, setFormVersion] = useState("");
  const [formOtaUrl, setFormOtaUrl] = useState("");
  const [formReleaseNotes, setFormReleaseNotes] = useState("");
  const [formIsActive, setFormIsActive] = useState(true);
  const [formIsLatest, setFormIsLatest] = useState(false);
  const [formMinRequiredVersion, setFormMinRequiredVersion] = useState("");
  const [formReleaseDate, setFormReleaseDate] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Fetch versions
  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await appVersionsService.fetchVersions(includeInactive);
      setVersions(Array.isArray(response) ? response : response?.data || []);
    } catch (err) {
      console.error("Error fetching versions:", err);
      setError("Greška pri učitavanju verzija");
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await appVersionsService.fetchStats();
      setStats(response);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
    fetchStats();
  }, [fetchVersions, fetchStats]);

  // Open modal for creating new version
  const handleCreateClick = () => {
    setEditingVersion(null);
    setFormVersion("");
    setFormOtaUrl("");
    setFormReleaseNotes("");
    setFormIsActive(true);
    setFormIsLatest(false);
    setFormMinRequiredVersion("");
    setFormReleaseDate("");
    setModalOpen(true);
  };

  // Open modal for editing version
  const handleEditClick = (version) => {
    setEditingVersion(version);
    setFormVersion(version.version);
    setFormOtaUrl(version.otaUrl || "");
    setFormReleaseNotes(version.releaseNotes || "");
    setFormIsActive(version.isActive);
    setFormIsLatest(version.isLatest);
    setFormMinRequiredVersion(version.minRequiredVersion || "");
    setFormReleaseDate(version.releaseDate ? version.releaseDate.slice(0, 10) : "");
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingVersion(null);
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formVersion.trim()) {
      toast.error("Verzija je obavezna");
      return;
    }
    if (!formOtaUrl.trim()) {
      toast.error("OTA URL je obavezan");
      return;
    }

    setFormSubmitting(true);
    try {
      const data = {
        version: formVersion.trim(),
        otaUrl: formOtaUrl.trim(),
        releaseNotes: formReleaseNotes.trim() || null,
        isActive: formIsActive,
        isLatest: formIsLatest,
        minRequiredVersion: formMinRequiredVersion.trim() || null,
        releaseDate: formReleaseDate || null,
      };

      if (editingVersion) {
        await appVersionsService.updateVersion(editingVersion.id, data);
        toast.success("Verzija uspešno ažurirana");
      } else {
        await appVersionsService.createVersion(data);
        toast.success("Verzija uspešno kreirana");
      }

      handleCloseModal();
      fetchVersions();
      fetchStats();
    } catch (err) {
      console.error("Error saving version:", err);
      toast.error(editingVersion ? "Greška pri ažuriranju verzije" : "Greška pri kreiranju verzije");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Open delete confirmation
  const handleDeleteClick = (version) => {
    setVersionToDelete(version);
    setDeleteModalOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!versionToDelete) return;

    try {
      await appVersionsService.deleteVersion(versionToDelete.id);
      toast.success("Verzija uspešno obrisana");
      setDeleteModalOpen(false);
      setVersionToDelete(null);
      fetchVersions();
      fetchStats();
    } catch (err) {
      console.error("Error deleting version:", err);
      toast.error("Greška pri brisanju verzije");
    }
  };

  // Set as latest
  const handleSetAsLatest = async (version) => {
    try {
      await appVersionsService.setAsLatest(version.id);
      toast.success(`Verzija ${version.version} postavljena kao latest`);
      fetchVersions();
      fetchStats();
    } catch (err) {
      console.error("Error setting as latest:", err);
      toast.error("Greška pri postavljanju verzije kao latest");
    }
  };

  // View devices on version
  const handleViewDevices = async (versionString) => {
    setSelectedVersionForDevices(versionString);
    setDevicesModalOpen(true);
    setDevicesLoading(true);
    try {
      const response = await appVersionsService.fetchDevicesOnVersion(versionString);
      setDevicesOnVersion(response?.data || response || []);
    } catch (err) {
      console.error("Error fetching devices:", err);
      toast.error("Greška pri učitavanju uređaja");
    } finally {
      setDevicesLoading(false);
    }
  };

  // Render loading state
  const renderLoading = () => (
    <div className={styles.loadingState}>
      <div className={styles.spinner} />
      <p>Učitavanje...</p>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className={styles.errorState}>
      <p>⚠️ {error}</p>
      <button className={styles.retryButton} onClick={fetchVersions}>
        Pokušaj ponovo
      </button>
    </div>
  );

  // Render stats card
  const renderStats = () => {
    if (!stats) return null;
    
    const totalDevices = stats.totalDevices || stats.versions?.reduce((sum, v) => sum + v.deviceCount, 0) || 0;
    const versionStats = stats.versions || [];

    return (
      <div className={styles.statsCard}>
        <div className={styles.statsHeader}>
          <h3>Distribucija verzija</h3>
          <span className={styles.totalDevices}>{totalDevices} uređaja ukupno</span>
        </div>
        <div className={styles.statsContent}>
          {versionStats.map((vs) => (
            <div key={vs.version} className={styles.statsRow}>
              <div className={styles.statsRowHeader}>
                <span className={styles.statsVersion}>
                  {vs.version}
                  {vs.isLatest && <span className={styles.latestBadge}>⭐ Latest</span>}
                  {!vs.isRegistered && <span className={styles.unregisteredBadge}>(neregistrovana)</span>}
                </span>
                <span 
                  className={styles.statsCount}
                  onClick={() => handleViewDevices(vs.version)}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {vs.deviceCount} uređaja
                </span>
              </div>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill}
                  style={{ 
                    width: `${totalDevices > 0 ? (vs.deviceCount / totalDevices) * 100 : 0}%`,
                    backgroundColor: vs.isLatest ? '#22C55E' : vs.isActive ? '#3B82F6' : '#9CA3AF'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render versions table
  const renderVersionsTable = () => (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Verzija</th>
            <th>Status</th>
            <th>OTA URL</th>
            <th>Min verzija</th>
            <th>Datum</th>
            <th>Akcije</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id} className={classNames(!v.isActive && styles.inactiveRow)}>
              <td>
                <div className={styles.versionCell}>
                  <span className={styles.versionNumber}>{v.version}</span>
                  {v.isLatest && <span className={styles.latestChip}>Latest</span>}
                </div>
              </td>
              <td>
                <span className={classNames(
                  styles.statusChip,
                  v.isActive ? styles.statusActive : styles.statusInactive
                )}>
                  {v.isActive ? "Aktivna" : "Neaktivna"}
                </span>
              </td>
              <td>
                <a 
                  href={v.otaUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.otaLink}
                  title={v.otaUrl}
                >
                  {truncateUrl(v.otaUrl)}
                </a>
              </td>
              <td>{v.minRequiredVersion || "-"}</td>
              <td>{formatDate(v.releaseDate)}</td>
              <td>
                <div className={styles.actions}>
                  {!v.isLatest && v.isActive && (
                    <button
                      className={styles.actionButton}
                      onClick={() => handleSetAsLatest(v)}
                      title="Postavi kao Latest"
                    >
                      ⭐
                    </button>
                  )}
                  <button
                    className={styles.actionButton}
                    onClick={() => handleEditClick(v)}
                    title="Izmeni"
                  >
                    ✏️
                  </button>
                  <button
                    className={classNames(styles.actionButton, styles.actionButtonDanger)}
                    onClick={() => handleDeleteClick(v)}
                    title="Obriši"
                  >
                    🗑️
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <DashboardLayout>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>App Verzije</h1>
          <div className={styles.headerActions}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              <span>Prikaži neaktivne</span>
            </label>
            <button className={styles.createButton} onClick={handleCreateClick}>
              + Nova verzija
            </button>
          </div>
        </div>

        {/* Stats */}
        {renderStats()}

        {/* Content */}
        {loading && renderLoading()}
        {error && !loading && renderError()}
        {!loading && !error && versions.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📦</div>
            <p>Nema registrovanih verzija</p>
            <button className={styles.createButton} onClick={handleCreateClick}>
              Dodaj prvu verziju
            </button>
          </div>
        )}
        {!loading && !error && versions.length > 0 && renderVersionsTable()}

        {/* Create/Edit Modal */}
        <BaseModal
          modalOpen={modalOpen}
          onClose={handleCloseModal}
          title={editingVersion ? "Izmeni verziju" : "Nova verzija"}
        >
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Verzija *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formVersion}
                  onChange={(e) => setFormVersion(e.target.value)}
                  placeholder="3.0.0"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Min Required Version</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formMinRequiredVersion}
                  onChange={(e) => setFormMinRequiredVersion(e.target.value)}
                  placeholder="2.5.0"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>OTA URL *</label>
              <input
                type="text"
                className={styles.input}
                value={formOtaUrl}
                onChange={(e) => setFormOtaUrl(e.target.value)}
                placeholder="https://s3.amazonaws.com/bucket/lpfr-3.0.0.zip"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Release Notes</label>
              <textarea
                className={styles.textarea}
                value={formReleaseNotes}
                onChange={(e) => setFormReleaseNotes(e.target.value)}
                placeholder="- Nova funkcionalnost&#10;- Bug fix"
                rows={4}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Release Date</label>
              <input
                type="date"
                className={styles.input}
                value={formReleaseDate}
                onChange={(e) => setFormReleaseDate(e.target.value)}
              />
            </div>

            <div className={styles.formRow}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                />
                <span>Aktivna</span>
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formIsLatest}
                  onChange={(e) => setFormIsLatest(e.target.checked)}
                />
                <span>⭐ Latest verzija</span>
              </label>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCloseModal}
                disabled={formSubmitting}
              >
                Otkaži
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={formSubmitting || !formVersion.trim() || !formOtaUrl.trim()}
              >
                {formSubmitting ? "Čuvanje..." : editingVersion ? "Sačuvaj" : "Kreiraj"}
              </button>
            </div>
          </form>
        </BaseModal>

        {/* Delete Confirmation Modal */}
        <BaseModal
          modalOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setVersionToDelete(null);
          }}
          title="Potvrda brisanja"
        >
          <div className={styles.deleteConfirmation}>
            <p>
              Da li ste sigurni da želite da obrišete verziju{" "}
              <strong>{versionToDelete?.version}</strong>?
            </p>
            {versionToDelete?.isLatest && (
              <p className={styles.deleteWarning}>
                ⚠️ Ovo je trenutno Latest verzija!
              </p>
            )}
            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => {
                  setDeleteModalOpen(false);
                  setVersionToDelete(null);
                }}
              >
                Otkaži
              </button>
              <button
                type="button"
                className={styles.deleteConfirmButton}
                onClick={handleConfirmDelete}
              >
                Obriši
              </button>
            </div>
          </div>
        </BaseModal>

        {/* Devices on Version Modal */}
        <BaseModal
          modalOpen={devicesModalOpen}
          onClose={() => {
            setDevicesModalOpen(false);
            setSelectedVersionForDevices(null);
            setDevicesOnVersion([]);
          }}
          title={`Uređaji na verziji ${selectedVersionForDevices}`}
        >
          <div className={styles.devicesModal}>
            {devicesLoading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner} />
                <p>Učitavanje...</p>
              </div>
            ) : devicesOnVersion.length === 0 ? (
              <p className={styles.noDevices}>Nema uređaja na ovoj verziji</p>
            ) : (
              <div className={styles.devicesList}>
                {devicesOnVersion.map((device) => (
                  <div 
                    key={device.id} 
                    className={styles.deviceItem}
                    onClick={() => history.push(`/fiscal-devices/${device.id}/info`)}
                  >
                    <span className={styles.deviceName}>{device.name}</span>
                    <span className={styles.deviceCompany}>
                      {device.businessUnit?.company?.name || "-"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </BaseModal>
      </div>
    </DashboardLayout>
  );
};

export default AppVersionsScreen;


